<template lang="html">
  <section
    v-if="items.length"
    class="product-recommendations w-full"
    aria-labelledby="product-recommendations-heading"
  >
    <div class="product-recommendations__header mb-6">
      <h2
        id="product-recommendations-heading"
        class="product-recommendations__title text-2xl font-bold tracking-tight text-highlighted sm:text-3xl"
      >
        Рекомендации
      </h2>
    </div>

    <div
      class="product-recommendations__list grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3 sm:gap-3 md:grid-cols-[repeat(auto-fill,minmax(240px,1fr))] md:gap-4"
    >
      <ProductCard
        v-for="(product, index) in items"
        :key="product.id"
        :value="product"
        :eager="index < EAGER_CARDS"
      />
    </div>

    <div
      ref="sentinelRef"
      class="product-recommendations__sentinel h-4 w-full"
    />

    <div
      v-if="pending"
      class="product-recommendations__loader mt-8 flex justify-center"
    >
      <AppLoader class="h-8 w-8" />
    </div>

    <p
      v-else-if="!hasAfter"
      class="mt-6 text-center text-sm text-muted"
    >
      Показаны все рекомендации
    </p>
  </section>
</template>

<script setup lang="ts">
import type { ProductDto, ProductsListGetRequestQuery } from '@repo/api/web/models'
import { ProductListItemDto } from '@repo/api/web/models'
import { useIntersectionObserver } from '@vueuse/core'

import { useEcommerce } from '#Analytics/composables/useEcommerce'
import { mapCatalogProduct } from '#Analytics/utils/ecommerceMapper'
import ProductCard from '#Products/components/Card/ProductCard.vue'
import AppLoader from '~/app/components/Loader/AppLoader.vue'

/**
 * PDP recommendations: catalog-wide cursor listing (not category-only).
 * Category-scoped lists on this demo exhaust in one page (≤8 items) so
 * infinite scroll never fires; catalog-wide + PAGE_SIZE leaves a next page.
 */
const PAGE_SIZE = 8
const EAGER_CARDS = 4

const props = defineProps<{
  value: ProductDto
}>()

const { service } = useProducts()

const productId = computed(() => props.value.id)

const { collection, pending, hasAfter, nextPage } = await useCursorListing<
  ProductsListGetRequestQuery,
  ProductListItemDto
>({
  // Unique per product; parent remounts this block with `:key="id"`.
  key: `pdp-recs-${productId.value}`,
  fetcher: queryParams => service.list(queryParams),
  transform: data =>
    ProductListItemDto
      .createFromCollection(data ?? [])
      .filter(item => item.id !== productId.value),
  initialQuery: {
    Limit: PAGE_SIZE,
  },
})

const items = computed(() => collection.value || [])

const sentinelRef = ref<HTMLElement | null>(null)

function tryLoadMore() {
  if (!hasAfter.value || pending.value) return
  nextPage()
}

useIntersectionObserver(
  sentinelRef,
  ([entry]) => {
    if (entry?.isIntersecting) tryLoadMore()
  },
  { threshold: 0.1, rootMargin: '200px' },
)

// If the sentinel is already in view after a batch (short last page), keep
// filling until hasAfter is false or the sentinel leaves the viewport.
watch([hasAfter, pending, items], () => {
  if (import.meta.server || pending.value || !hasAfter.value) return

  nextTick(() => {
    const el = sentinelRef.value
    if (!el) return
    const rect = el.getBoundingClientRect()
    const inView = rect.top < window.innerHeight + 200
    if (inView) tryLoadMore()
  })
})

const ecommerce = useEcommerce()
const impressedProductIds = new Set<number>()

watch(items, (list) => {
  if (import.meta.server) return
  const fresh = list
    .map((product, index) => ({ product, position: index + 1 }))
    .filter(({ product }) => product.id != null && !impressedProductIds.has(product.id))
  if (!fresh.length) return

  fresh.forEach(({ product }) => impressedProductIds.add(product.id!))
  ecommerce.impressions(
    fresh.map(({ product, position }) =>
      mapCatalogProduct(product, { list: 'Рекомендации', position }),
    ),
  )
}, { flush: 'post' })
</script>
